import type { OnDestroy } from '@angular/core';
import { Directive, inject, signal } from '@angular/core';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';

import { AccountSelectors } from '@core/account-data';
import { Analytics } from '@core/analytics-types';
import { filterBoolean } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { OfferFull } from '@core/offers-types';
import { DialogService, NotificationsController } from '@core/user-interface-legacy';

import type { ActionsVisibilityBreakpoint } from './bulk-actions-toolbar/bulk-actions-toolbar.component';
import type {
  BulkAction,
  BulkActionId,
  BulkItemBase,
  GetBulkActionsProps,
} from './bulk-actions.types';

@Directive({ standalone: true })
export abstract class BulkActionsController<
  BulkItem extends BulkItemBase = BulkItemBase,
> implements OnDestroy {
  public selectedItems$: BehaviorSubject<BulkItem[]> = new BehaviorSubject([]);
  public active$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public selectedIdsDict$: BehaviorSubject<Record<number | string, true>> = new BehaviorSubject({});
  public ready$: Observable<boolean>;
  public recalculateActions$ = new Subject<void>();

  protected availableActions: BulkAction[];
  protected ngOnDestroy$ = new Subject<void>();

  protected notificationsCtrl = inject(NotificationsController);
  protected store = inject(Store);
  protected analytics = inject(Analytics);
  protected dialogService = inject(DialogService);
  protected i18n = inject(I18nService);

  private actionsInProgress = signal<Partial<Record<number | BulkActionId<string>, boolean>>>({});

  protected constructor() {
    this.ready$ = this.store.select(AccountSelectors.currentCompany).pipe(
      filterBoolean,
      take(1),
      map(company => !!company),
      takeUntil(this.ngOnDestroy$),
    );

    this.selectedItems$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(items => {
      const selectedIds = items.reduce<Record<number | string, true>>((acc, item) => {
        acc[item.id] = true;
        return acc;
      }, {});
      this.selectedIdsDict$.next(selectedIds);
      if (items.length === 0) {
        this.active$.next(false);
      } else if (!this.active$.getValue()) {
        this.active$.next(true);
      }
    });

    this.active$
      .asObservable()
      .pipe(
        filter(v => !v && this.selectedItems$.getValue().length > 0),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(() => this.clearSelection());
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public get buttons(): BulkAction[] {
    return this.availableActions;
  }

  public get active(): boolean {
    return this.active$.getValue();
  }

  public abstract isActionPossible(action: BulkAction): boolean;
  public abstract getBulkActions(props: GetBulkActionsProps): BulkAction[];
  public abstract dispatchAction(data: {
    action: BulkAction;
    offer?: Partial<Pick<OfferFull, 'id' | 'kind' | 'status' | 'hireAssistant'>> | null;
    customCandidates?: BulkItem[];
    requisition?: { id: number } | null;
  }): void;
  public abstract getActionsVisibilityByBreakpoint(
    viewType?: string,
  ): ActionsVisibilityBreakpoint[];

  public isItemSelected(item: BulkItem): boolean {
    return this.selectedIdsDict$.getValue()[item.id];
  }

  public selectItem(item: BulkItem, toggle: boolean = !this.isItemSelected(item)): void {
    const nextSelectedItems = this.selectedItems$
      .getValue()
      .filter(i => i.id !== item.id)
      .concat(toggle ? [item] : []);
    this.selectedItems$.next(nextSelectedItems);
  }

  public selectItems(items: BulkItem[], toggle: boolean): void {
    const itemsIds = items.map(item => item.id);
    const nextSelectedItems = this.selectedItems$
      .getValue()
      .filter(i => !itemsIds.includes(i.id))
      .concat(toggle ? items : []);
    this.selectedItems$.next(nextSelectedItems);
  }

  public clearSelection(): void {
    this.selectedItems$.next([]);
  }

  public toggleActive(): void {
    this.active$.next(!this.active);
  }

  public isActionInProgress(action: BulkAction): boolean {
    return this.actionsInProgress()[action.id]!;
  }

  protected markBulkActionAsInProgress(action: BulkAction): void {
    if (!this.isActionInProgress(action)) {
      this.actionsInProgress.update(inProgress => ({ ...inProgress, [action.id]: true }));
    }
  }

  protected markBulkActionAsCompleted(action: BulkAction): void {
    this.actionsInProgress.update(inProgress => ({ ...inProgress, [action.id]: false }));
  }

  protected dispatchActionError(action: BulkAction, error: any): void {
    console.error('error', action, error);
  }
}
